import { type AdminUserSelectDto } from '@@types/api.types';
import { ActiveFilters } from '@components/data-table-filter/components/active-filters';
import { FilterActions } from '@components/data-table-filter/components/filter-actions';
import { FilterSelector } from '@components/data-table-filter/components/filter-selector';
import type {
  Column,
  DataTableFilterActions,
  FilterStrategy,
  FiltersState,
} from '@components/data-table-filter/core/types';
import { UserRoleBadge } from '@components/shared/user-role-badge.component';
import { UserStatusBadge } from '@components/shared/user-status-badge.component';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { ClearableSearchInput } from '@components/ui/clearable-search-input';
import { Separator } from '@components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/ui/tooltip';
import {
  ColumnDef,
  SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { ArrowDownWideNarrowIcon, ArrowUpDown, ArrowUpNarrowWideIcon, BadgeCheckIcon, Loader2, X } from 'lucide-react';
import React from 'react';

type AdminUsersTableProps = {
  actions: DataTableFilterActions;
  columns: Column<AdminUserSelectDto>[];
  filters: FiltersState;
  isLoading: boolean;
  onSearchChange: (search: string | null) => void;
  onSelectUser: (userId: string) => void;
  onSortingChange: (sorting: SortingState) => void;
  search: string | null;
  sorting: SortingState;
  strategy: FilterStrategy;
  users: AdminUserSelectDto[];
};

const formatDateTime = (value: string) => {
  try {
    return format(new Date(value), 'MMM d, yyyy');
  } catch {
    return '—';
  }
};

type SearchFilterPillProps = {
  onClear: () => void;
  value: string;
};

const SearchFilterPill: React.FC<SearchFilterPillProps> = (props) => {
  const { onClear, value } = props;

  return (
    <div className="flex h-7 items-center rounded-2xl border border-border bg-background shadow-xs text-xs">
      <span className="px-2 text-muted-foreground">Search</span>
      <Separator orientation="vertical" />
      <span className="px-2">{value}</span>
      <Separator orientation="vertical" />
      <Button
        className="rounded-none rounded-r-2xl text-xs w-7 h-full"
        onClick={onClear}
        type="button"
        variant="ghost"
      >
        <X className="size-4 -translate-x-0.5" />
      </Button>
    </div>
  );
};

export const AdminUsersTable: React.FC<AdminUsersTableProps> = (props) => {
  const {
    actions,
    columns: filterColumns,
    filters,
    isLoading,
    onSearchChange,
    onSelectUser,
    onSortingChange,
    search,
    sorting,
    strategy,
    users,
  } = props;
  const hasSearchFilter = Boolean(search?.trim());

  const tableColumns = React.useMemo<ColumnDef<AdminUserSelectDto>[]>(
    () => [
      {
        id: 'user',
        accessorFn: (row) => row.profile?.fullName ?? row.email ?? '',
        header: 'Name / Email',
        sortingFn: 'text',
        cell: ({ row }) => {
          const user = row.original;

          return (
            <div className="flex flex-col gap-1">
              <div className="text-sm font-medium text-gray-900">
                <span>{user.profile?.fullName ?? user.email}</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-500">{user.email}</span>
                {user.emailVerified && (
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <BadgeCheckIcon className="size-5 text-white fill-blue-600 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="font-normal">
                        <p>Email verified</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </div>
          );
        },
      },
      {
        id: 'role',
        accessorFn: (row) => row.profile?.role ?? '',
        header: 'Role',
        sortingFn: 'text',
        cell: ({ row }) => {
          const role = row.original.profile?.role;

          if (!role) return <span className="text-xs text-gray-500">—</span>;

          return (
            <UserRoleBadge
              role={role}
              size="xs"
            />
          );
        },
      },
      {
        id: 'organizations',
        accessorFn: (row) => row.companies?.[0]?.name ?? '',
        header: 'Organization',
        sortingFn: 'text',
        cell: ({ row }) => {
          const user = row.original;
          const organizations = (user.companies ?? []).filter(Boolean);

          if (organizations.length === 0) {
            return <span className="text-xs text-gray-500">—</span>;
          }

          const firstOrg = organizations[0];

          if (organizations.length === 1) {
            return <span className="text-xs text-gray-900">{firstOrg?.name ?? '—'}</span>;
          }

          return (
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-900">{firstOrg?.name ?? '—'}</span>
              <Badge
                size="xs"
                variant="secondary"
              >
                +{organizations.length - 1}
              </Badge>
            </div>
          );
        },
      },
      {
        id: 'status',
        accessorFn: (row) => row.status ?? '',
        header: 'Status',
        sortingFn: 'text',
        cell: ({ row }) => {
          const user = row.original;

          return (
            <UserStatusBadge
              size="xs"
              status={user.status}
            />
          );
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Joined',
        sortingFn: 'datetime',
        cell: ({ getValue }) => <span className="text-xs text-gray-600">{formatDateTime(getValue<string>())}</span>,
      },
    ],
    [],
  );

  const table = useReactTable({
    columns: tableColumns,
    data: users,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange,
    state: { sorting },
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <FilterSelector
            actions={actions}
            columns={filterColumns}
            filters={filters}
            strategy={strategy}
          />
          <ClearableSearchInput
            clearValue={null}
            onChange={onSearchChange}
            placeholder="Search by name, email, or organization"
            value={search}
          />
        </div>
        {(filters.length > 0 || hasSearchFilter) && (
          <div className="flex w-full items-start justify-between gap-2">
            <div className="flex md:flex-wrap gap-2 w-full flex-1">
              {hasSearchFilter && (
                <SearchFilterPill
                  onClear={() => onSearchChange(null)}
                  value={search ?? ''}
                />
              )}
              <ActiveFilters
                actions={actions}
                columns={filterColumns}
                filters={filters}
                strategy={strategy}
              />
            </div>
            <FilterActions
              actions={actions}
              hasFilters={filters.length > 0}
            />
          </div>
        )}
      </div>
      {isLoading ? (
        <div className="flex items-center justify-center h-48 rounded-xl border">
          <Loader2 className="size-6 animate-spin text-gray-500" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    if (header.isPlaceholder) {
                      return <TableHead key={header.id} />;
                    }

                    const canSort = header.column.getCanSort();
                    const sorted = header.column.getIsSorted();

                    return (
                      <TableHead
                        key={header.id}
                        className="text-xs font-semibold text-gray-500"
                      >
                        {canSort ? (
                          <button
                            className="flex items-center gap-1 text-xs font-semibold text-gray-600"
                            onClick={header.column.getToggleSortingHandler()}
                            type="button"
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {sorted === 'asc' && <ArrowUpNarrowWideIcon className="size-3.5" />}
                            {sorted === 'desc' && <ArrowDownWideNarrowIcon className="size-3.5" />}
                            {!sorted && <ArrowUpDown className="size-3.5 text-gray-400" />}
                          </button>
                        ) : (
                          flexRender(header.column.columnDef.header, header.getContext())
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => onSelectUser(row.original.id)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={tableColumns.length}
                    className="h-24 text-center text-sm text-gray-500"
                  >
                    No users found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
